// Torneos archivados en las listas del superadmin (Eduardo 2026-10-03).
//
// Gobierno de torneos y Fees de plataforma se llenaban de filas «ID 245
// archivado por herramienta de gobierno · archived». Ahora los archivados se
// ocultan por default y una casilla «Mostrar archivados (N)» los muestra.
// Aquí vive la lógica pura (sin React) que comparten ambas pantallas y la
// tarjeta «Torneos recientes» del inicio del superadmin.
//
// Archivado = estado "archived" (hoy solo lo usan las lápidas de gobierno) o
// el nombre de la lápida, por si alguna trae otro estado. Un torneo CERRADO
// (terminado, solo lectura) NO es archivado: se sigue viendo.

/** Lápida que deja la herramienta de gobierno al eliminar un torneo
 * (nombre «ID N archivado por herramienta de gobierno»). */
export function isGovernanceTombstone(name: string | null | undefined): boolean {
  const value = (name ?? "").trim();
  return value.startsWith("ID ") && value.includes("archivado por herramienta de gobierno");
}

export interface ArchivableTournament {
  status?: string | null;
  name?: string | null;
}

export function isArchivedTournament(row: ArchivableTournament): boolean {
  if ((row.status ?? "").trim().toLowerCase() === "archived") return true;
  return isGovernanceTombstone(row.name);
}

export interface ArchivedSplit<T> {
  /** Filas a pintar, en el mismo orden en que llegaron. */
  visible: T[];
  /** Archivados en total (para la casilla «Mostrar archivados (N)»). */
  archivedCount: number;
  /** Archivados que quedaron fuera de `visible` (0 con la casilla marcada). */
  hiddenCount: number;
}

/** Separa los archivados sin tocar la lista original. */
export function splitArchived<T extends ArchivableTournament>(
  rows: readonly T[],
  showArchived: boolean,
): ArchivedSplit<T> {
  const visible: T[] = [];
  let archivedCount = 0;
  for (const row of rows) {
    const archived = isArchivedTournament(row);
    if (archived) archivedCount += 1;
    if (showArchived || !archived) visible.push(row);
  }
  return { visible, archivedCount, hiddenCount: showArchived ? 0 : archivedCount };
}

/** «1 archivado oculto» / «3 archivados ocultos». */
export function archivedHiddenNote(count: number): string {
  return count === 1 ? "1 archivado oculto" : `${count} archivados ocultos`;
}

/** Estado del torneo en español; uno desconocido se muestra tal cual. */
export function superadminStatusLabel(status: string | null | undefined): string {
  switch ((status ?? "").trim().toLowerCase()) {
    case "draft":
      return "Borrador";
    case "active":
      return "Activo";
    case "closed":
      return "Cerrado";
    case "archived":
      return "Archivado";
    default:
      return status ?? "";
  }
}

/** La casilla se recuerda por pantalla y solo en este navegador. */
export const SHOW_ARCHIVED_KEYS = {
  gobierno: "torneos123_superadmin_mostrar_archivados_gobierno",
  fees: "torneos123_superadmin_mostrar_archivados_fees",
} as const;

export function readShowArchived(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    // Navegación privada, almacenamiento bloqueado o sin navegador: ocultos.
    return false;
  }
}

export function writeShowArchived(key: string, value: boolean): void {
  try {
    if (value) {
      window.localStorage.setItem(key, "1");
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // No se pudo guardar: la casilla vale solo por esta visita.
  }
}
